/**
 * `?redirect=` validation for Login, Register and the Google sign-in return (A5, §6).
 *
 * Only a same-origin path to an app route on the allowlist is followed; anything else
 * (another origin, a protocol-relative or backslash URL, a control character, the auth
 * pages themselves) is dropped and the caller falls back to Početna.
 */
import { ROUTES } from '@/config/routes';

const MAX_LENGTH = 512;

/** Routes a visitor may be sent back to; `/sessions/:id` is allowed through its prefix. */
const ALLOWED_PATHS: readonly string[] = [
  ROUTES.home,
  ROUTES.subscription,
  ROUTES.guided,
  ROUTES.acoustic,
  ROUTES.favorites,
  ROUTES.history,
  ROUTES.profile,
  ROUTES.journal,
  ROUTES.pendulum,
  ROUTES.chakra,
  ROUTES.book,
  ROUTES.bookSuccess,
];
const ALLOWED_PREFIXES: readonly string[] = [`${ROUTES.guided}/`, '/book/read/'];

// eslint-disable-next-line no-control-regex
const UNSAFE = /[\u0000- \u007f\\]|%5c/i;

function isAllowedPath(pathname: string): boolean {
  return ALLOWED_PATHS.includes(pathname) || ALLOWED_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

/** The redirect as a same-origin path (`/pricing?plan=annual`), or null if it is not safe to follow. */
export function safeRedirect(raw: string | null | undefined, origin: string = window.location.origin): string | null {
  if (!raw || raw.length > MAX_LENGTH) return null;
  if (!raw.startsWith('/') || raw.startsWith('//') || UNSAFE.test(raw)) return null;
  let url: URL;
  try {
    url = new URL(raw, origin);
  } catch {
    return null;
  }
  if (url.origin !== origin || !isAllowedPath(url.pathname)) return null;
  return `${url.pathname}${url.search}${url.hash}`;
}

/** `path` with `?redirect=` when there is one (Login and Register link to each other with it). */
export function withRedirect(path: string, redirect: string | null): string {
  return redirect ? `${path}?redirect=${encodeURIComponent(redirect)}` : path;
}
